import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Breadcrumbs from '../components/Breadcrumbs';
import BackButton from '../components/BackButton';
import ProductGallery from '../components/ProductGallery';
import QuantitySelector from '../components/QuantitySelector';
import { useCart } from '../context/CartContext';
import { Star, ShoppingCart, Truck, Shield, RefreshCw, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { country, productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products/${productId}`);
        if (res.ok) {
          const data = await res.json();
          setProduct(data);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-aug-cream">
        <Loader2 className="w-10 h-10 animate-spin text-aug-blue" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-aug-gray-dark mb-4">Produto nao encontrado</h1>
          <button
            onClick={() => navigate(`/${country}/products/catalog`)}
            className="bg-aug-blue text-white px-6 py-3 rounded-lg"
            data-testid="back-to-catalog-btn"
          >
            Voltar ao Catalogo
          </button>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: country?.toUpperCase() || 'BR', link: `/${country}` },
    { label: 'Produtos', link: `/${country}/products` },
    { label: 'Catalogo', link: `/${country}/products/catalog` },
    { label: product.name, link: null }
  ];

  const price = product.sale_price || product.regular_price || 0;
  const hasDiscount = product.sale_price && product.regular_price && product.sale_price < product.regular_price;
  const discountPercent = hasDiscount 
    ? Math.round(((product.regular_price - product.sale_price) / product.regular_price) * 100)
    : 0;

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      <WhatsAppButton />
      
      <div className="pt-20">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="max-w-[1400px] mx-auto px-xl py-4xl">
          <BackButton to={`/${country}/products/catalog`} label="Voltar ao Catalogo" />

          {/* Product Hero */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-3xl">
            {/* Gallery */}
            <div>
              <ProductGallery images={product.images || []} video={product.video_url} />
            </div>

            {/* Product Info */}
            <div>
              <span className="text-sm text-aug-blue font-medium">{product.category_name}</span>
              <h1 className="text-4xl font-bold text-aug-gray-dark mt-2 mb-4" data-testid="product-name">{product.name}</h1>

              {/* Partner Info */}
              {product.partner_name && (
                <p className="text-sm text-aug-gray-medium mb-4">
                  Vendido por <span className="font-semibold text-aug-blue">{product.partner_name}</span>
                </p>
              )}

              {/* Rating */}
              {product.reviews_count > 0 && (
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.floor(product.average_rating)
                            ? 'fill-aug-warning text-aug-warning'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-aug-gray-dark font-medium">{product.average_rating}</span>
                  <span className="text-aug-gray-medium">({product.reviews_count} avaliacoes)</span>
                </div>
              )}

              {/* SKU */}
              {product.sku && (
                <p className="text-xs text-aug-gray-medium mb-4">SKU: {product.sku}</p>
              )}

              {/* Price */}
              <div className="bg-aug-cream p-6 rounded-xl mb-6">
                <div className="flex items-baseline gap-3">
                  {price > 0 ? (
                    <span className="text-5xl font-bold text-aug-green" data-testid="product-price">R$ {price.toFixed(2)}</span>
                  ) : (
                    <span className="text-3xl font-bold text-aug-gray-medium">Consulte o preco</span>
                  )}
                  {hasDiscount && (
                    <>
                      <span className="text-2xl text-aug-gray-medium line-through">
                        R$ {product.regular_price.toFixed(2)}
                      </span>
                      <span className="bg-aug-green-success text-white px-3 py-1 rounded-full text-sm font-bold">
                        -{discountPercent}%
                      </span>
                    </>
                  )}
                </div>
                {product.in_stock ? (
                  <p className="text-aug-green-success font-semibold mt-2">Em estoque ({product.stock_quantity} unidades)</p>
                ) : (
                  <p className="text-aug-error font-semibold mt-2">Consultar disponibilidade</p>
                )}
              </div>

              {/* Add to Cart */}
              <div className="flex gap-4 mb-6">
                <QuantitySelector
                  quantity={quantity}
                  onIncrease={() => setQuantity(Math.min(quantity + 1, product.stock_quantity || 99))}
                  onDecrease={() => setQuantity(Math.max(quantity - 1, 1))}
                  max={product.stock_quantity || 99}
                />
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                    addedToCart
                      ? 'bg-aug-green-success text-white'
                      : 'bg-aug-blue hover:bg-aug-blue-dark text-white'
                  }`}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {addedToCart ? 'Adicionado!' : 'Adicionar ao Carrinho'}
                </button>
              </div>

              {/* Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-aug-gray-dark">
                  <Truck className="w-5 h-5 text-aug-blue" />
                  <span>Frete gratis acima de R$ 200</span>
                </div>
                <div className="flex items-center gap-3 text-aug-gray-dark">
                  <Shield className="w-5 h-5 text-aug-blue" />
                  <span>Garantia de 12 meses</span>
                </div>
                <div className="flex items-center gap-3 text-aug-gray-dark">
                  <RefreshCw className="w-5 h-5 text-aug-blue" />
                  <span>Troca gratis em 30 dias</span>
                </div>
              </div>

              {/* Short Description */}
              <div className="border-t border-aug-sand pt-6">
                <p className="text-aug-gray-medium leading-relaxed">{product.short_description || product.description}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          {product.description && (
            <section className="mt-4xl bg-white rounded-xl p-3xl">
              <h2 className="text-3xl font-bold text-aug-gray-dark mb-4">Descricao Detalhada</h2>
              <p className="text-aug-gray-dark leading-relaxed whitespace-pre-line">{product.description}</p>
            </section>
          )}

          {/* Partner Promotional Banner */}
          {product.partner_id === 'rocha-climatizacao' && (
            <div className="mt-4xl" data-testid="partner-promo-banner">
              <img
                src="/rocha_banner_lg.jpg"
                alt="Rocha Climatização - A Maior Distribuidora de Peças LG do Tocantins"
                className="w-full rounded-xl shadow-lg object-cover"
              />
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
